import type { ReactNode } from 'react';
import { Box, Stack, Text } from '@mantine/core';

import { LinkValue } from './LinkValue';

export interface InfoField {
  label: string;
  value: string;
  onClick?: () => void;
}

interface InfoCardProps {
  title?: string;
  fields: InfoField[];
  columns?: number;
  borderColor?: string;
  valueFontSize?: number;
  mobileValueFontSize?: number;
  trailing?: ReactNode;
}

const divider = <div style={{ height: 1, backgroundColor: 'var(--color-border-soft)' }} />;

const FieldValue = ({ field, fz }: { field: InfoField; fz: number }) =>
  field.onClick ? (
    <LinkValue value={field.value} onClick={field.onClick} />
  ) : (
    <Text fz={fz} style={{ color: 'var(--color-text-primary)' }}>
      {field.value}
    </Text>
  );

export const InfoCard = ({
  title,
  fields,
  columns,
  borderColor = 'var(--color-border-soft)',
  valueFontSize = 15,
  mobileValueFontSize = 14,
  trailing
}: InfoCardProps) => {
  return (
    <Stack
      gap={12}
      p={20}
      style={{
        borderRadius: 12,
        border: `1px solid ${borderColor}`,
        backgroundColor: 'var(--color-bg-surface)'
      }}
    >
      {title && (
        <Text visibleFrom="sm" fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {title}
        </Text>
      )}

      {columns ? (
        // Prava CSS grid (ne ručno dijeljenje u stupce) — polja se prirodno
        // popunjavaju red po red, lijevo-desno, bez "rupa" kad broj polja
        // nije djeljiv s brojem stupaca (samo zadnji red ostaje nepotpun).
        <Box
          visibleFrom="sm"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            columnGap: 24,
            rowGap: 12,
            width: '100%'
          }}
        >
          {fields.map((field) => (
            <Stack key={field.label} gap={2} style={{ minWidth: 0 }}>
              <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                {field.label}
              </Text>
              <FieldValue field={field} fz={valueFontSize} />
            </Stack>
          ))}
        </Box>
      ) : (
        <Box visibleFrom="sm" style={{ display: 'flex', flexWrap: 'wrap', gap: 40, width: '100%' }}>
          {fields.map((field) => (
            <Stack key={field.label} gap={6} style={{ flex: '1 1 160px', minWidth: 160 }}>
              <Text
                fz={11}
                fw={600}
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
              >
                {field.label}
              </Text>
              <FieldValue field={field} fz={valueFontSize} />
            </Stack>
          ))}
        </Box>
      )}

      <Stack hiddenFrom="sm" gap={columns ? 10 : 12} style={{ width: '100%' }}>
        {fields.map((field, index) => (
          <Stack key={field.label} gap={columns ? 10 : 12}>
            {index > 0 && divider}
            <Stack gap={2}>
              <Text
                fz={columns ? 10 : 11}
                fw={600}
                style={{ color: 'var(--color-text-muted)', letterSpacing: columns ? 0 : 0.4 }}
              >
                {field.label}
              </Text>
              <FieldValue field={field} fz={mobileValueFontSize} />
            </Stack>
          </Stack>
        ))}
      </Stack>

      {trailing && (
        <>
          {divider}
          {trailing}
        </>
      )}
    </Stack>
  );
};
